import React, { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { OrnamentDivider, Fleuron } from "@/components/Ornament";
import { Lemon } from "@/components/ItalianOrnaments";
import { X } from "lucide-react";

const STORAGE_KEY = "roze_season_closed_dismissed_v2";

export const SeasonClosedModal = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(STORAGE_KEY);
      if (!dismissed) {
        const timer = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {}
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        data-testid="season-closed-modal"
        className="max-w-[94vw] sm:max-w-lg md:max-w-xl w-full p-0 overflow-hidden bg-[#FFFBF3] border border-gold/40 [&>button]:hidden"
      >
        <div className="relative p-8 sm:p-10 md:p-14 paper-grain text-center">
          <button
            type="button"
            onClick={handleClose}
            data-testid="season-closed-modal-close"
            aria-label="Close"
            className="absolute top-4 right-4 p-2 text-foreground/50 hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>

          <Lemon className="w-14 h-14 mx-auto mb-5 text-[#E0B23E] opacity-90" />

          <div className="overline text-[#1F5C8A] mb-4 flex items-center justify-center gap-3">
            <Fleuron className="h-2.5 w-2.5 text-gold" />
            {t("season_closed.eyebrow")}
            <Fleuron className="h-2.5 w-2.5 text-gold" />
          </div>

          <DialogTitle asChild>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-[1.1] tracking-tight italic text-foreground">
              {t("season_closed.title")}
            </h2>
          </DialogTitle>

          <OrnamentDivider className="my-6 md:my-7" />

          <DialogDescription asChild>
            <p className="text-base md:text-lg text-foreground/75 leading-relaxed font-light">
              {t("season_closed.body")}
            </p>
          </DialogDescription>

          <p className="mt-5 md:mt-6 font-serif italic text-xl md:text-2xl text-[#1F5C8A]">
            {t("season_closed.reopen")}
          </p>

          <button
            type="button"
            onClick={handleClose}
            data-testid="season-closed-modal-cta"
            className="mt-8 md:mt-10 inline-flex items-center justify-center gap-3 px-9 py-3.5 bg-[#1F5C8A] text-white hover:opacity-90 transition-opacity text-[11px] uppercase tracking-[0.24em]"
          >
            {t("season_closed.cta")}
          </button>

          <p className="mt-6 text-xs md:text-sm text-foreground/55 italic font-light">
            {t("season_closed.signature")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
